"use client"

import React, { useEffect, useState, useMemo, useRef } from "react"
import Icon from "../../Icon"
import api from "../../../utils/api"
import { Link } from "react-router-dom"
import PdfViewer from "../../PdfViewer"

function ImageCarousel({ images, onImageClick }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  // eslint-disable-next-line no-unused-vars
  const scrollRef = useRef(null)

  if (!images || images.length === 0) {
    return (
      <div className="relative w-full h-48 bg-gradient-to-br from-slate-100 to-slate-200 rounded-t-2xl flex items-center justify-center">
        <div className="text-center">
          <Icon name="image" size="2rem" className="text-slate-400 mb-2" />
          <p className="text-xs text-slate-500">No photos available</p>
        </div>
      </div>
    )
  }

  const handlePrev = (e) => {
    e.stopPropagation()
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNext = (e) => {
    e.stopPropagation()
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="relative w-full h-48 bg-slate-900 rounded-t-2xl overflow-hidden group cursor-pointer" onClick={() => onImageClick && onImageClick(images, currentIndex)}>
      {/* Main Image */}
      <img
        src={images[currentIndex].url}
        alt={`Unit ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all shadow-lg z-10"
          >
            <Icon name="chevron-left" size="1rem" className="text-slate-800" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all shadow-lg z-10"
          >
            <Icon name="chevron-right" size="1rem" className="text-slate-800" />
          </button>
        </>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded-full font-medium">
          {currentIndex + 1}/{images.length}
        </div>
      )}

      {/* Expand Icon */}
      <div className="absolute top-2 right-2 w-8 h-8 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <Icon name="arrows-angle-expand" size="0.875rem" className="text-white" />
      </div>

      {/* Dot Indicators */}
      {images.length > 1 && images.length <= 5 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation()
                setCurrentIndex(idx)
              }}
              className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-4' : 'bg-white/50 hover:bg-white/75'
                }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FullScreenImageViewer({ images, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0)

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') handlePrev()
      if (e.key === 'ArrowRight') handleNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  if (!images || images.length === 0) return null

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-50"
      >
        <Icon name="x" size="1.5rem" />
      </button>

      {/* Image Counter */}
      <div className="absolute top-4 left-4 px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm rounded-full font-medium z-50">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Main Image */}
      <div className="relative w-full h-full flex items-center justify-center p-8">
        <img
          src={images[currentIndex].url}
          alt={`View ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        />
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-50"
          >
            <Icon name="chevron-left" size="1.5rem" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors z-50"
          >
            <Icon name="chevron-right" size="1.5rem" />
          </button>
        </>
      )}

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-[90vw] overflow-x-auto">
          <div className="flex gap-2 px-4">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === currentIndex ? 'border-white scale-110' : 'border-white/30 opacity-60 hover:opacity-100'
                  }`}
              >
                <img src={img.url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <nav className={`w-full bg-white/95 backdrop-blur-sm sticky top-0 z-50 transition-all duration-300 border-b ${
      scrolled ? "border-gray-200/80 shadow-sm" : "border-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          <Link to="/" className="flex items-center gap-3 group">
            <img src="/logofinalrounded.png" alt="RecordLOGS" className="w-8 h-8 rounded-lg object-cover shadow-sm group-hover:shadow-md transition-shadow" />
            <span className="text-xl font-bold text-slate-800">RecordLOGS</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-green-600 transition-colors font-medium text-sm flex items-center gap-1.5">
              <Icon name="arrow-left" size="0.8rem" className="text-slate-400" /> Home
            </Link>
            <Link to="/login" className="bg-green-500 text-white px-5 py-2 rounded-lg hover:bg-green-600 transition-all duration-200 font-semibold text-sm shadow-sm hover:shadow-md">
              Sign In
            </Link>
          </div>

          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Toggle menu">
            <Icon name={menuOpen ? "x" : "list"} size="1.25rem" />
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-gray-100 py-3">
            <div className="flex flex-col gap-1">
              <Link to="/" className="px-3 py-2.5 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors text-sm flex items-center gap-2" onClick={() => setMenuOpen(false)}>
                <Icon name="house" size="0.875rem" /> Home
              </Link>
              <Link to="/login" className="px-3 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm text-center font-semibold flex items-center justify-center gap-2" onClick={() => setMenuOpen(false)}>
                <Icon name="box-arrow-in-right" size="0.875rem" /> Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

function PageHeader({ count, onShowLeaseAgreement }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-green-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-green-400/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white font-black text-xs px-4 py-1.5 rounded-lg tracking-widest mb-6 shadow-lg uppercase">
            <Icon name="buildings" size="0.8rem" />
            For Rent
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3 leading-tight">
            Find Your Next Home
          </h1>
          <p className="text-slate-400 text-base sm:text-lg mb-8 max-w-md mx-auto">
            {count > 0
              ? <><span className="text-green-400 font-bold">{count}</span> {count === 1 ? "unit" : "units"} available right now</>
              : "No units available right now — check back soon"}
          </p>
          <button
            onClick={onShowLeaseAgreement}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/15 text-white px-5 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium"
          >
            <Icon name="file-earmark-text" size="0.875rem" />
            View Lease Agreement
          </button>
        </div>
      </div>
    </section>
  )
}

const normalizeText = (value) => String(value || '').trim()

const formatUnitTypeLabel = (value) => {
  const normalized = normalizeText(value)
  if (normalized.toLowerCase() === 'studio') return 'Studio/BedSitter'
  return normalized
}

const normalizeTypeFilterQuery = (value) => {
  const normalized = normalizeText(value).toLowerCase()
  if (normalized === 'studio/bedsitter') return 'studio'
  return normalizeText(value)
}

const includesText = (value, query) => {
  if (!query) return true
  return normalizeText(value).toLowerCase().includes(normalizeText(query).toLowerCase())
}

const unitMatchesFilters = (unit, filters, ignoreKey) => {
  const matchCity = ignoreKey === 'city' ? true : includesText(unit.property?.cityTown, filters.city)
  const matchLocation = ignoreKey === 'location' ? true : includesText(unit.property?.location, filters.location)
  const matchArea = ignoreKey === 'area' ? true : includesText(unit.property?.area, filters.area)
  const matchType = ignoreKey === 'type' ? true : includesText(unit.type || '', normalizeTypeFilterQuery(filters.type))
  const isVacant = String(unit.status || 'Vacant').toLowerCase() === 'vacant' && (!unit.tenant)
  const isUnlockedProperty = !unit.property?.locked
  return matchCity && matchLocation && matchArea && matchType && isVacant && isUnlockedProperty
}

function SearchableSelect({ label, value, onChange, placeholder, options, formatOption = (option) => option }) {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const query = (value || '').toLowerCase()
  const filteredOptions = options.filter((option) => {
    if (!query) return true
    const rawOption = option.toLowerCase()
    const formattedOption = formatOption(option).toLowerCase()
    return rawOption.includes(query) || formattedOption.includes(query)
  }).slice(0, 5)

  const handleSelect = (option) => {
    onChange(option)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={formatOption(value || '')}
          onChange={(e) => {
            onChange(e.target.value)
            setIsOpen(true)
          }}
          onClick={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full h-10 px-3 pr-10 bg-white border border-slate-300 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-shadow shadow-sm"
        />
        <div
          className="absolute right-0 top-0 h-full w-10 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon name="chevron-down" size="1rem" className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option}
                onClick={() => handleSelect(option)}
                className="px-3 py-2.5 text-sm text-slate-700 hover:bg-green-50 hover:text-green-700 cursor-pointer transition-colors border-b last:border-0 border-slate-50"
              >
                {formatOption(option)}
              </div>
            ))
          ) : (
            <div className="px-3 py-2.5 text-sm text-slate-400 italic">No matches found</div>
          )}
        </div>
      )}
    </div>
  )
}

function FiltersBar({ city, setCity, location, setLocation, area, setArea, type, setType, showSaved, setShowSaved, savedCount, cityOptions, locationOptions, areaOptions, typeOptions }) {
  const hasFilters = city || location || area || type

  const clearAll = () => {
    setCity(""); setLocation(""); setArea(""); setType("")
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
              <Icon name="funnel" className="text-green-600" size="0.875rem" />
            </div>
            <span className="text-sm font-semibold text-slate-700">Filter Units</span>
          </div>
          <div className="flex items-center gap-2">
            {hasFilters && (
              <button onClick={clearAll} className="text-xs text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1">
                <Icon name="x-circle" size="0.75rem" /> Clear
              </button>
            )}
            <button
              onClick={() => setShowSaved(!showSaved)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium text-xs transition-all ${
                showSaved ? "bg-green-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              <Icon name={showSaved ? "bookmark-fill" : "bookmark"} size="0.75rem" />
              Saved
              {savedCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${showSaved ? "bg-green-600" : "bg-green-100 text-green-700"}`}>
                  {savedCount}
                </span>
              )}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <SearchableSelect label="Unit Type" value={type} onChange={setType} placeholder="All types" options={typeOptions} formatOption={formatUnitTypeLabel} listId="unit-type-options" />
          <SearchableSelect label="City / Town" value={city} onChange={setCity} placeholder="Any city" options={cityOptions} listId="unit-city-options" />
          <SearchableSelect label="Location" value={location} onChange={setLocation} placeholder="Estate, road…" options={locationOptions} listId="unit-location-options" />
          <SearchableSelect label="Area" value={area} onChange={setArea} placeholder="Neighbourhood" options={areaOptions} listId="unit-area-options" />
        </div>
      </div>
    </div>
  )
}

function ContactModal({ unit, onClose }) {
  if (!unit) return null
  const companyName = unit.property?.managementCompany?.name || 'Management Company'
  const companyPhone = unit.property?.managementCompany?.phone || unit.property?.phone || 'N/A'
  const propertyName = unit.property?.name || 'Property'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-slate-200 w-[90%] max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Contact Management Company</h3>
          <button className="p-2 text-slate-500 hover:text-slate-700" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="space-y-3">
          <div className="text-slate-600 text-sm">For unit <span className="font-medium text-slate-800">{propertyName} - {unit.label}</span></div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="w-10 h-10 rounded-lg bg-green-100 text-green-700 flex items-center justify-center">
              <Icon name="building" />
            </div>
            <div>
              <div className="text-slate-800 font-medium">{companyName}</div>
              <div className="text-slate-600 text-sm">{companyPhone}</div>
            </div>
          </div>
          <a
            href={companyPhone && companyPhone !== 'N/A' ? `tel:${companyPhone}` : undefined}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium ${companyPhone && companyPhone !== 'N/A' ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
            aria-disabled={!companyPhone || companyPhone === 'N/A'}
          >
            <Icon name="telephone" size="0.875rem" /> Call
          </a>
        </div>
      </div>
    </div>
  )
}

function UnitCard({ unit, onContact, isSaved, onToggleSave, onImageClick }) {
  const propertyName = unit.property?.name || "Property"
  const cityTown = unit.property?.cityTown
  const location = unit.property?.location
  const rent = Number(unit.rentAmount || 0)
  const type = formatUnitTypeLabel(unit.type || '') || 'Unit'

  return (
    <div className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-green-200 transition-all duration-300 overflow-hidden">
      <ImageCarousel images={unit.images} onImageClick={onImageClick} />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-800 group-hover:text-green-600 transition-colors line-clamp-1 text-base">
              {propertyName} — {unit.label}
            </h3>
            {(cityTown || location) && (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <Icon name="geo-alt" size="0.7rem" />
                {[cityTown, location].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
          <div className="shrink-0">
            <span className="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-lg">
              <Icon name="building" size="0.7rem" /> {type}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-2xl font-black text-slate-800">{rent.toLocaleString()}</span>
            <span className="text-slate-400 text-xs font-medium ml-1">/mo</span>
          </div>
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Icon name="cash-stack" size="0.75rem" className="text-green-500" />
            Rent
          </span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onToggleSave && onToggleSave(unit)}
            className={`flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
              isSaved ? "bg-green-500 text-white hover:bg-green-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <Icon name={isSaved ? "bookmark-fill" : "bookmark"} size="0.875rem" />
            {isSaved ? "Saved" : "Save"}
          </button>
          <button
            onClick={() => onContact && onContact(unit)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-green-200 text-green-600 hover:bg-green-500 hover:text-white hover:border-green-500 transition-all duration-200 text-sm font-semibold"
          >
            <Icon name="telephone" size="0.75rem" />
            Contact
          </button>
        </div>
      </div>
    </div>
  )
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null

  const pages = []
  const showPages = 5
  let start = Math.max(1, currentPage - Math.floor(showPages / 2))
  let end = Math.min(totalPages, start + showPages - 1)

  if (end - start + 1 < showPages) {
    start = Math.max(1, end - showPages + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Icon name="chevron-left" size="0.875rem" />
      </button>

      {start > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            1
          </button>
          {start > 2 && <span className="text-slate-400">...</span>}
        </>
      )}

      {pages.map(page => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`px-3 py-2 rounded-lg border transition-colors ${page === currentPage
            ? 'bg-green-500 border-green-500 text-white'
            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
        >
          {page}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="text-slate-400">...</span>}
          <button
            onClick={() => onPageChange(totalPages)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Icon name="chevron-right" size="0.875rem" />
      </button>
    </div>
  )
}

function UnitsGrid({ units, currentPage, totalPages, onPageChange, onContact, savedUnits, onToggleSave, onImageClick, totalFiltered }) {
  if (!units || units.length === 0) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Icon name="search" size="1.5rem" className="text-green-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No Units Found</h3>
          <p className="text-slate-500 text-sm max-w-xs mx-auto">No available units match your current filters. Try adjusting your search or check back later.</p>
        </div>
      </div>
    )
  }

  return (
    <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-500">
          Showing <span className="font-semibold text-slate-700">{units.length}</span>
          {totalFiltered > units.length && <> of <span className="font-semibold text-slate-700">{totalFiltered}</span></>} units
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {units.map((u) => (
          <UnitCard
            key={u._id || `${u.property?.name}-${u.label}`}
            unit={u}
            onContact={onContact}
            isSaved={savedUnits.includes(u._id)}
            onToggleSave={onToggleSave}
            onImageClick={onImageClick}
          />
        ))}
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
    </section>
  )
}

function AvailableUnitsView() {
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [city, setCity] = useState("")
  const [location, setLocation] = useState("")
  const [area, setArea] = useState("")
  const [type, setType] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [contactUnit, setContactUnit] = useState(null)
  const [showLeaseAgreementModal, setShowLeaseAgreementModal] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  const [savedUnits, setSavedUnits] = useState(() => {
    // Load saved units from session storage on initial render
    try {
      const saved = sessionStorage.getItem('savedUnits')
      return saved ? JSON.parse(saved) : []
    } catch (_) {
      try { sessionStorage.removeItem('savedUnits') } catch (_) {}
      return []
    }
  })
  const [fullScreenImages, setFullScreenImages] = useState(null)
  const [fullScreenInitialIndex, setFullScreenInitialIndex] = useState(0)
  const unitsPerPage = 12

  // Save to session storage whenever savedUnits changes
  useEffect(() => {
    sessionStorage.setItem('savedUnits', JSON.stringify(savedUnits))
  }, [savedUnits])

  // Function to toggle save/unsave a unit
  const toggleSaveUnit = (unit) => {
    setSavedUnits(prev => {
      if (prev.includes(unit._id)) {
        // Remove from saved
        return prev.filter(id => id !== unit._id)
      } else {
        // Add to saved
        return [...prev, unit._id]
      }
    })
  }

  // Function to handle image click for full-screen view
  const handleImageClick = (images, initialIndex) => {
    setFullScreenImages(images)
    setFullScreenInitialIndex(initialIndex)
  }

  useEffect(() => {
    let ignore = false
    const fetchUnits = async () => {
      try {
        setLoading(true)
        setError("")
        const res = await api.get(`/frp/public/available-units`)
        if (!ignore) setUnits(Array.isArray(res?.data?.units) ? res.data.units : [])
      } catch (e) {
        setError("Failed to load units")
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    fetchUnits()
    return () => { ignore = true }
  }, [])

  const { filtered, totalPages, paginatedUnits } = useMemo(() => {
    const filtered = units.filter(u => {
      const matchFilters = unitMatchesFilters(u, { city, location, area, type })
      const matchSaved = showSaved ? savedUnits.includes(u._id) : true
      return matchFilters && matchSaved
    })

    const totalPages = Math.ceil(filtered.length / unitsPerPage)
    const startIndex = (currentPage - 1) * unitsPerPage
    const paginatedUnits = filtered.slice(startIndex, startIndex + unitsPerPage)

    return { filtered, totalPages, paginatedUnits }
  }, [units, city, location, area, type, currentPage, unitsPerPage, showSaved, savedUnits])

  const { cityOptions, locationOptions, areaOptions, typeOptions } = useMemo(() => {
    const filters = { city, location, area, type }
    const sets = {
      city: new Set(),
      location: new Set(),
      area: new Set(),
      type: new Set(),
    }

    units.forEach((unit) => {
      if (unitMatchesFilters(unit, filters, 'city')) {
        const value = normalizeText(unit.property?.cityTown)
        if (value) sets.city.add(value)
      }
      if (unitMatchesFilters(unit, filters, 'location')) {
        const value = normalizeText(unit.property?.location)
        if (value) sets.location.add(value)
      }
      if (unitMatchesFilters(unit, filters, 'area')) {
        const value = normalizeText(unit.property?.area)
        if (value) sets.area.add(value)
      }
      if (unitMatchesFilters(unit, filters, 'type')) {
        const value = normalizeText(unit.type)
        if (value) sets.type.add(value)
      }
    })

    const sortOptions = (values) => Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

    return {
      cityOptions: sortOptions(sets.city),
      locationOptions: sortOptions(sets.location),
      areaOptions: sortOptions(sets.area),
      typeOptions: sortOptions(sets.type),
    }
  }, [units, city, location, area, type])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [city, location, area, type, showSaved])

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <PageHeader count={filtered.length} onShowLeaseAgreement={() => setShowLeaseAgreementModal(true)} />



      <FiltersBar city={city} setCity={setCity} location={location} setLocation={setLocation} area={area} setArea={setArea} type={type} setType={setType} showSaved={showSaved} setShowSaved={setShowSaved} savedCount={savedUnits.length} cityOptions={cityOptions} locationOptions={locationOptions} areaOptions={areaOptions} typeOptions={typeOptions} />

      {loading && (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading available units...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
            <Icon name="exclamation-triangle" className="inline mr-2" />
            {error}
          </div>
        </div>
      )}

      <UnitsGrid
        units={paginatedUnits}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onContact={(u) => setContactUnit(u)}
        savedUnits={savedUnits}
        onToggleSave={toggleSaveUnit}
        onImageClick={handleImageClick}
        totalFiltered={filtered.length}
      />
      <ContactModal unit={contactUnit} onClose={() => setContactUnit(null)} />

      {/* Full Screen Image Viewer */}
      {fullScreenImages && (
        <FullScreenImageViewer
          images={fullScreenImages}
          initialIndex={fullScreenInitialIndex}
          onClose={() => setFullScreenImages(null)}
        />
      )}

      {/* Lease Agreement Modal */}
      {showLeaseAgreementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800">Lease Agreement</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = '/documents/FRP_Lease_Agreement_Homepage.pdf';
                      link.download = 'FRP_Lease_Agreement_Homepage.pdf';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    Download PDF
                  </button>
                  <button
                    onClick={() => setShowLeaseAgreementModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl px-2"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Content - PDF Viewer */}
            <div className="flex-1">
              <div className="w-full bg-gray-50 border border-gray-300 rounded" style={{ height: '75vh' }}>
                <PdfViewer url="/documents/FRP_Lease_Agreement_Homepage.pdf" height="75vh" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AvailableUnitsView
